import React, { useState, useCallback, useEffect } from 'react';
import { Form, Row, Col, Button, Tabs, Tab } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMeter, faEye } from '@fortawesome/pro-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';

import PlugCustomTemplateUsageRules from './PlugCustomTemplateUsageRules';
import PlugCustomTemplateCostRules from './PlugCustomTemplateCostRules';
import SelectPlugLoadedAccountModal from '../../modals/PlugCustomTemplateModals/SelectPlugLoadedAccountModal';
import ViewPlugLineItemsModal from '../../modals/PlugCustomTemplateModals/ViewPlugLineItemsModal';
import ApiServiceServerless from '../../../xhr_libs';
import { DEFAULT_TEMPLATE } from './helpers';

export default function PlugCustomTemplate(props) {
  const {
    activeTemplate,
    setTemplates,
    providerAccounts,
    organizationId,
    showToast,
    isSaving,
    setIsSaving,
  } = props;

  const [activeTab, setActiveTab] = useState('template-details');

  const [localTemplate, setLocalTemplate] = useState(DEFAULT_TEMPLATE);
  const [editTemplate, setEditTemplate] = useState(DEFAULT_TEMPLATE);

  const [loadedAccount, setLoadedAccount] = useState(null);
  const [statements, setStatements] = useState([]);
  const [statementIdx, setStatementIdx] = useState(0);
  const [accIdx, setAccIdx] = useState(0);
  const [meterIdx, setMeterIdx] = useState(0);

  const [results, setResults] = useState([]);

  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    let temp_template = { ...activeTemplate };

    setLocalTemplate({ ...temp_template });
    setEditTemplate({ ...temp_template });
  }, [activeTemplate]);

  const onFieldChange = useCallback((fieldName, value) => {
    setEditTemplate((ep) => {
      ep[fieldName] = value;
      return { ...ep };
    });
  }, []);

  const handlePayload = () => {
    let payload = {};

    // basic
    if (localTemplate.name !== editTemplate.name) {
      payload.name = editTemplate.name;
    }
    if (localTemplate.utility_provider !== editTemplate.utility_provider) {
      payload.utility_provider = editTemplate.utility_provider;
    }

    //usages
    if (localTemplate.usage_default !== editTemplate.usage_default) {
      payload.usage_default = editTemplate.usage_default;
    }

    // costs
    if (localTemplate.cost_default !== editTemplate.cost_default) {
      payload.cost_default = editTemplate.cost_default;
    }

    if (
      localTemplate.cost1_name !== editTemplate.cost1_name ||
      localTemplate.cost2_name !== editTemplate.cost2_name ||
      localTemplate.cost3_name !== editTemplate.cost3_name
    ) {
      payload.has_cost_name = true;
      payload.cost1_name = editTemplate.cost1_name;
      payload.cost2_name = editTemplate.cost2_name;
      payload.cost3_name = editTemplate.cost3_name;
    }

    if (localTemplate.has_cost_name !== editTemplate.has_cost_name) {
      payload.has_cost_name = editTemplate.has_cost_name;
      if (!editTemplate.has_cost_name) {
        payload.cost1_name = null;
        payload.cost2_name = null;
        payload.cost3_name = null;
      }
    }

    return payload;
  };

  const handleSaveTemplate = () => {
    setIsSaving(true);
    const payload = handlePayload();
    ApiServiceServerless.post(
      `/custom_templates/${activeTemplate.id}`,
      payload,
      { authorization_id: organizationId }
    )
      .then((res) => {
        setTemplates((prev) =>
          prev.map((template) => {
            if (template['id'] !== activeTemplate.id) return template;
            return res.data;
          })
        );
        showToast('success', 'Success', `Template was updated.`);
      })
      .catch((error) => {
        showToast('danger', 'Error', error);
        throw error;
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <div style={{ minHeight: '350px', width: '100%' }}>
        {loadedAccount && (
          <>
            Loaded Account: {loadedAccount}{' '}
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                margin: '0em 0.25em',
                verticalAlign: 'text-bottom',
                cursor: 'pointer',
                color: 'var(--et_tab_grey)',
              }}
              icon={faEye}
              title={'View Statements'}
              onClick={() => setShowViewModal(true)}
            />
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                lineHeight: 'inherit',
                color: 'red',
                cursor: 'pointer',
              }}
              icon={faTimesCircle}
              title={'Clear Account'}
              disabled={isSaving}
              onClick={() => {
                setLoadedAccount(null);
                setStatements([]);
                setResults([]);
              }}
            />
          </>
        )}
        {!loadedAccount && (
          <>
            Select Account
            <FontAwesomeIcon
              style={{
                fontSize: '18px',
                margin: '0 0.35em',
                verticalAlign: 'text-bottom',
                cursor: 'pointer',
                color: 'var(--et_tab_grey)',
                opacity: '1.0',
              }}
              icon={faMeter}
              title={'Add Utility Account'}
              onClick={() => setShowSelectModal(true)}
            />
          </>
        )}

        <Tabs
          id='custom-template-tabs'
          activeKey={activeTab}
          onSelect={setActiveTab}
          className='et-second-tabs'
        >
          <Tab eventKey='template-details' title={'Template Details'}>
            <Row>
              <Col md={1}></Col>
              <Col md={10}>
                <Form>
                  <Row>
                    <Form.Group controlId='template.name' as={Col}>
                      <Form.Label>
                        Template Name
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        required
                        value={editTemplate.name || ''}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId='template.utility_provider' as={Col}>
                      <Form.Label>
                        Utility Provider
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        required
                        value={editTemplate.utility_provider || ''}
                        onChange={(e) =>
                          onFieldChange('utility_provider', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group controlId='template.usage_default' as={Col}>
                      <Form.Label>
                        Use Default Usage
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        required
                        value={editTemplate.usage_default}
                        onChange={(e) =>
                          onFieldChange(
                            'usage_default',
                            true ? e.target.value === 'true' : false
                          )
                        }
                      >
                        <option key={`location-opt-true`} value={true}>
                          True
                        </option>
                        <option key={`location-opt-false`} value={false}>
                          False
                        </option>
                      </Form.Control>
                    </Form.Group>
                    <Col></Col>
                  </Row>
                  <Row>
                    <Form.Group controlId='template.cost_default' as={Col}>
                      <Form.Label>
                        Use Default Cost
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        required
                        value={editTemplate.cost_default}
                        onChange={(e) =>
                          onFieldChange(
                            'cost_default',
                            true ? e.target.value === 'true' : false
                          )
                        }
                      >
                        <option key={`location-opt-true`} value={true}>
                          True
                        </option>
                        <option key={`location-opt-false`} value={false}>
                          False
                        </option>
                      </Form.Control>
                    </Form.Group>

                    <Form.Group controlId='template.has_cost_name' as={Col}>
                      <Form.Label>
                        Has Custom Cost Names
                        <span className='text-warning font-weight-bold pl-1'>
                          *
                        </span>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        required
                        value={editTemplate.has_cost_name}
                        onChange={(e) =>
                          onFieldChange(
                            'has_cost_name',
                            true ? e.target.value === 'true' : false
                          )
                        }
                      >
                        <option key={`location-opt-false`} value={false}>
                          False
                        </option>
                        <option key={`location-opt-true`} value={true}>
                          True
                        </option>
                      </Form.Control>
                    </Form.Group>
                  </Row>

                  {editTemplate.has_cost_name && (
                    <Row>
                      <Form.Group controlId='template.cost1_name' as={Col}>
                        <Form.Label>
                          Cost 1 Name
                          <span className='text-warning font-weight-bold pl-1'>
                            *
                          </span>
                        </Form.Label>
                        <Form.Control
                          required
                          value={editTemplate.cost1_name || ''}
                          onChange={(e) =>
                            onFieldChange('cost1_name', e.target.value)
                          }
                        />
                      </Form.Group>
                      <Form.Group controlId='template.cost2_name' as={Col}>
                        <Form.Label>
                          Cost 2 Name
                          <span className='text-warning font-weight-bold pl-1'>
                            *
                          </span>
                        </Form.Label>
                        <Form.Control
                          required
                          value={editTemplate.cost2_name || ''}
                          onChange={(e) =>
                            onFieldChange('cost2_name', e.target.value)
                          }
                        />
                      </Form.Group>
                      <Form.Group controlId='template.cost3_name' as={Col}>
                        <Form.Label>
                          Cost 3 Name
                          <span className='text-warning font-weight-bold pl-1'>
                            *
                          </span>
                        </Form.Label>
                        <Form.Control
                          required
                          value={editTemplate.cost3_name || ''}
                          onChange={(e) =>
                            onFieldChange('cost3_name', e.target.value)
                          }
                        />
                      </Form.Group>
                    </Row>
                  )}
                  <div style={{ width: '100%' }}>
                    <Button
                      disabled={isSaving}
                      onClick={() => handleSaveTemplate()}
                      className='float-right'
                    >
                      Save
                    </Button>
                    <Button
                      disabled={isSaving}
                      onClick={() => setEditTemplate({ ...localTemplate })}
                      className='float-right'
                      variant='outline-danger'
                      style={{ marginLeft: '10px', marginRight: '10px' }}
                    >
                      Clear Changes
                    </Button>
                  </div>
                </Form>
                <Col md={1}></Col>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey='template-usages' title={'Usages'}>
            <PlugCustomTemplateUsageRules
              activeTemplate={activeTemplate}
              setTemplates={setTemplates}
              organizationId={organizationId}
              showToast={showToast}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          </Tab>

          <Tab eventKey='template-costs' title={'Costs'}>
            <PlugCustomTemplateCostRules
              activeTemplate={activeTemplate}
              setTemplates={setTemplates}
              organizationId={organizationId}
              showToast={showToast}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
            />
          </Tab>
        </Tabs>
      </div>
      <SelectPlugLoadedAccountModal
        show={showSelectModal}
        onHide={() => {
          setShowSelectModal(false);
        }}
        providerAccounts={providerAccounts}
        organizationId={organizationId}
        setLoadedAccount={setLoadedAccount}
        setStatements={setStatements}
      />
      <ViewPlugLineItemsModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        activeTemplateId={activeTemplate.id}
        organizationId={organizationId}
        statements={statements}
        statementIdx={statementIdx}
        setStatementIdx={setStatementIdx}
        accIdx={accIdx}
        setAccIdx={setAccIdx}
        meterIdx={meterIdx}
        setMeterIdx={setMeterIdx}
        results={results}
        setResults={setResults}
      />
    </>
  );
}
